/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, MutableRefObject } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Select } from 'antd';
import { ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Area, Line } from 'recharts';
import { formatDateTime, formatNumber, formatNumberWithCommas, isNotNullUndefinedBlank } from '../helpers/common';
import { format, parseISO } from 'date-fns';
import { StockData, StockOptionsHighestOpenInterestData, ProcessedStockOptionsHighestOpenInterestData } from '../types/stock';
import { useFetchStockOptionsStockSymbols, useFetchStockOptionsHighestOpenInterest } from '../services/stock.service';
import { IResponseModel } from '../types/response';
import { storage } from '../utils/storage';

const processStockOptionsHighestOIData = (
    optionHighestOiData: MutableRefObject<{
        calls: StockOptionsHighestOpenInterestData[];
        puts: StockOptionsHighestOpenInterestData[];
    }>): ProcessedStockOptionsHighestOpenInterestData[] => {

    const allData = [
        ...optionHighestOiData.current.calls,
        ...optionHighestOiData.current.puts,
    ];

    const maxCallOI = Math.max(...optionHighestOiData.current.calls.map(d => d.openInterest));
    const maxPutOI = Math.max(...optionHighestOiData.current.puts.map(d => d.openInterest));

    const groupedData = allData.reduce((acc, curr) => {
        const date = curr.expirationDate;
        if (!acc[date]) {
            acc[date] = { calls: [], puts: [] };
        }
        if (curr.optionType === 'Call') {
            acc[date].calls.push(curr);
        } else {
            acc[date].puts.push(curr);
        }
        return acc;
    }, {} as Record<string, { calls: StockOptionsHighestOpenInterestData[]; puts: StockOptionsHighestOpenInterestData[] }>);

    return Object.entries(groupedData)
        .map(([date, { calls, puts }]) => {
            const maxOICall = calls.reduce(
                (max, curr) => (curr.openInterest > max.openInterest ? curr : max),
                calls[0] || { strikePrice: 0, openInterest: 0 }
            );
            const maxOIPut = puts.reduce(
                (max, curr) => (curr.openInterest > max.openInterest ? curr : max),
                puts[0] || { strikePrice: 0, openInterest: 0 }
            );
            return {
                expirationDate: format(parseISO(date), 'MMM dd, yyyy'),
                callStrike: maxOICall.strikePrice,
                putStrike: maxOIPut.strikePrice,
                callOI: maxOICall.openInterest,
                putOI: maxOIPut.openInterest,
                callOpacity: maxOICall.openInterest / maxCallOI,
                putOpacity: maxOIPut.openInterest / maxPutOI
            };
        })
        .sort((a, b) => new Date(a.expirationDate).getTime() - new Date(b.expirationDate).getTime());
};

const OptionGraph = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [stocks, setStocksList] = useState<any[]>([]);
    const [stockSymbolOpen, setStockSymbolOpen] = useState(false);
    const isAlertShown = useRef(false);
    const parentStockSymbol = useRef<string>('');
    const isScrapingMode = useRef(false);
    const optionHighestOiData = useRef<{
        calls: StockOptionsHighestOpenInterestData[];
        puts: StockOptionsHighestOpenInterestData[];
    }>({ calls: [], puts: [] });

    //#region freeScanCount changes

    const FREE_SCAN_LIMIT = 1;
    const freeScanCount = useRef(0);
    const isAuthenticatedUser = useRef(false);
    const isGuestUser = useRef(false);

    const isFreeScanAvailable = () => {
        isAuthenticatedUser.current = JSON.parse(storage.getLocalProperty("isAuthenticated") ?? false);
        isGuestUser.current = JSON.parse(storage.getLocalProperty("isGuestUser") ?? false);
        freeScanCount.current = JSON.parse(storage.getLocalProperty("freeScanCount") ?? 0);
        if (isAuthenticatedUser.current || isScrapingMode.current) return true;
        if (isGuestUser.current && (freeScanCount.current < FREE_SCAN_LIMIT)) {
            storage.setLocalProperty("freeScanCount", freeScanCount.current += 1);
            isAlertShown.current = false;
            return true;
        } else {
            resetOptionGraph();
            if (!isAlertShown.current) {
                alert("Please Sign in with Discord to unlock another scan.");
                isAlertShown.current = true;
            }
            return false;
        }
    };

    //#endregion freeScanCount changes

    const [loading, setLoading] = useState<boolean>(false);
    const [lastPrice, setLastPrice] = useState<number | string>(0);

    const getStockOptionsStocksList = useFetchStockOptionsStockSymbols({
        onSuccess: (response: IResponseModel<StockData>) => {
            if (response.statusCode === 200) {
                const options = response.data.map((stock) => ({
                    label: stock.stockSymbol,
                    value: stock.stockSymbol,
                    lastPrice: stock.lastPrice,
                }));
                setStocksList(options);
            } else {
                setStocksList([]);
            }
            setLoading(false);
        },
        onError: () => {
            setLoading(false);
        },
    });

    const getStockOptionsHighestOpenInterest = useFetchStockOptionsHighestOpenInterest({
        onSuccess: (response: IResponseModel<StockOptionsHighestOpenInterestData>) => {
            if (response.statusCode === 200) {
                const formattedData = response.data.map((item, index) => ({
                    ...item,
                    key: index.toString(),
                }));
                const calls = formattedData.filter(item => item.optionType === 'Call');
                const puts = formattedData.filter(item => item.optionType === 'Put');
                optionHighestOiData.current = { calls, puts };
            } else {
                optionHighestOiData.current = { calls: [], puts: [] };
            }
            setLoading(false);
        },
        onError: (error) => {
            console.error("Error fetching stock options details:", error);
            optionHighestOiData.current = { calls: [], puts: [] };
            setLoading(false);
        },
    });

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const parentStockSymbolFromUrl = params.get("symbol")?.toUpperCase() || "";
        isScrapingMode.current = params.get("scrapingMode") === "true";
        if (isNotNullUndefinedBlank(parentStockSymbolFromUrl) && (parentStockSymbolFromUrl !== parentStockSymbol.current)) {
            if (isFreeScanAvailable()) {
                parentStockSymbol.current = parentStockSymbolFromUrl;
            }
        }
    }, [location.search, navigate]);

    useEffect(() => {
        setLoading(true);
        if (stocks.length <= 0) getStockOptionsStocksList.mutate({});
        if (parentStockSymbol.current) {
            getStockOptionsHighestOpenInterest.mutate({ parentStockSymbol: parentStockSymbol.current });
        }
    }, [parentStockSymbol.current]);

    useEffect(() => {
        if (parentStockSymbol.current && stocks.length > 0) {
            const selectedStock = stocks.find((stock) => stock.value === parentStockSymbol.current);
            setLastPrice(selectedStock?.lastPrice || null);
        }
    }, [parentStockSymbol.current, stocks]);

    const resetOptionGraph = () => {
        parentStockSymbol.current = "";
        optionHighestOiData.current = { calls: [], puts: [] };
        setLastPrice(0);
        setLoading(false);
        navigate("/options/graph", { replace: true });
    }

    const handleStockSymbolChange = (value?: string) => {
        isAlertShown.current = false;
        if (!value) {
            resetOptionGraph();
            return;
        }
        if (isFreeScanAvailable()) {
            parentStockSymbol.current = value;
            setStockSymbolOpen(false);
            navigate(`/options/graph?symbol=${value}`, { replace: true });
            const selectedStock = stocks.find((stock) => stock.value === value);
            setLastPrice(selectedStock?.lastPrice || null);
        }
    };

    //#region unused-code
    // const StockOptionsHighestOpenInterestChart = () => {
    //     const callsData = optionHighestOiData.current.calls.map(item => ({
    //         date: formatDateTime(new Date(item.expirationDate), "DD-MMS-YYYY"),
    //         strikePrice: item.strikePrice,
    //         optionType: 'Call',
    //     }));

    //     const putsData = optionHighestOiData.current.puts.map(item => ({
    //         date: formatDateTime(new Date(item.expirationDate), "DD-MMS-YYYY"),
    //         strikePrice: item.strikePrice,
    //         optionType: 'Put',
    //     }));

    //     const combinedData = [...callsData, ...putsData].sort((a, b) => {
    //         return new Date(a.date).getTime() - new Date(b.date).getTime();
    //     });

    //     const groupedDataByDate = combinedData.reduce((acc: { [key: string]: { date: string, calls: any[], puts: any[] } }, curr: any) => {
    //         if (!acc[curr.date]) {
    //             acc[curr.date] = { date: curr.date, calls: [], puts: [] };
    //         }
    //         if (curr.optionType === 'Call') {
    //             acc[curr.date].calls.push(curr);
    //         } else {
    //             acc[curr.date].puts.push(curr);
    //         }
    //         return acc;
    //     }, {});

    //     const flatData = Object.values(groupedDataByDate).flatMap((group: { calls: any[], puts: any[] }) => {
    //         return [
    //             ...group.calls,
    //             ...group.puts,
    //         ];
    //     });

    //     const maxStrikePrice = Math.max(
    //         ...flatData.map(item => item.strikePrice),
    //     );

    //     const stockOptionsHighestOpenInterestConfig = {
    //         data: flatData,
    //         xField: 'date',
    //         yField: 'strikePrice',
    //         colorField: 'optionType',
    //         seriesField: 'optionType',
    //         point: {
    //             shapeField: 'circle',
    //             sizeField: 2,
    //         },
    //         scale: {
    //             color: {
    //                 range: ['#1890FF', '#FF4D4F'],
    //             },
    //         },
    //         axis: {
    //             x: { title: 'Expiry Date', labelAutoHide: 'greedy' },
    //             y: {
    //                 title: 'Strike Price',
    //                 min: 0,
    //                 max: maxStrikePrice,
    //             },
    //             style: {
    //                 fontSize: 18,
    //                 fontFamily: "Poppins",
    //             },
    //         },
    //     };

    //     return <Line {...stockOptionsHighestOpenInterestConfig} />;
    // };
    //#endregion

    const callsData = optionHighestOiData.current.calls.map(item => ({
        date: formatDateTime(new Date(item.expirationDate), "DD-MMS-YY"),
        strikePrice: item.strikePrice,
        optionType: 'Call',
        NoOfOpenContract: item.openInterest,
    }));

    const putsData = optionHighestOiData.current.puts.map(item => ({
        date: formatDateTime(new Date(item.expirationDate), "DD-MMS-YY"),
        strikePrice: item.strikePrice,
        optionType: 'Put',
        NoOfOpenContract: item.openInterest,
    }));

    const uniqueDates = Array.from(new Set([...callsData.map(item => item.date), ...putsData.map(item => item.date)]));

    const combinedData = uniqueDates.map(date => {
        const callsForDate = callsData.filter(item => item.date === date);
        const putsForDate = putsData.filter(item => item.date === date);

        return {
            date,
            calls: callsForDate.length ? callsForDate[0] : { strikePrice: null, optionType: 'Call', NoOfOpenContract: 0 },
            puts: putsForDate.length ? putsForDate[0] : { strikePrice: null, optionType: 'Put', NoOfOpenContract: 0 },
        };
    }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const groupedDataByDate = combinedData.reduce((acc: { [key: string]: { date: string, calls: any[], puts: any[] } }, curr: any) => {
        if (!acc[curr.date]) {
            acc[curr.date] = { date: curr.date, calls: [], puts: [] };
        }
        if (curr.optionType === 'Call') {
            acc[curr.date].calls.push(curr);
        } else {
            acc[curr.date].puts.push(curr);
        }
        return acc;
    }, {});

    const flatData = Object.values(groupedDataByDate).flatMap((group: { calls: any[], puts: any[] }) => {
        return [
            ...group.calls,
            ...group.puts,
        ];
    });

    const CustomTooltip = ({ active, payload }: any) => {
        if ((active && payload) && payload.length > 0) {
            const { expirationDate, callStrike, putStrike, callOI, putOI, } = payload[0].payload;

            return (
                <div
                    className="custom-tooltip"
                    style={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E0E0E0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        padding: '10px',
                        fontFamily: 'Arial, sans-serif',
                        fontSize: '13px',
                        lineHeight: '1.5',
                        color: '#333',
                        maxWidth: '320px',
                    }}>
                    <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontWeight: '600', color: '#555' }}>
                            Date: <span style={{ color: '#000', fontWeight: 'bold' }}>{expirationDate}</span>
                        </div>
                    </div>
                    <div
                        style={{
                            marginBottom: '12px',
                            padding: '5px',
                            backgroundColor: '#F0F8FF',
                            borderRadius: '6px',
                            borderLeft: '4px solid #1890FF',
                        }}>
                        <div style={{ color: '#1890FF', fontWeight: '600' }}>
                            Call:- Strike <span className="text-black font-bold">${callStrike}</span> Call:- OI : <span className="text-black font-bold">{formatNumber(callOI)}</span>
                        </div>
                    </div>
                    <div
                        style={{
                            padding: '5px',
                            backgroundColor: '#FFF5F5',
                            borderRadius: '6px',
                            borderLeft: '4px solid #FF4D4F',
                        }}>
                        <div style={{ color: '#FF4D4F', fontWeight: '600' }}>
                            Put:- Strike <span className="text-black font-bold">${putStrike}</span> Put:- OI : <span className="text-black font-bold">{formatNumber(putOI)}</span>
                        </div>
                    </div>
                </div>
            );
        }
        return <></>;
    };

    const processedData = processStockOptionsHighestOIData(optionHighestOiData);

    return (
        <div className="p-2.5 lg:p-4 rounded-xl shadow-box">
            <div className="flex items-center justify-center lg:justify-start gap-3 mb-5">
                <h4 className="hidden md:block font-bold text-lg text-black opacity-85 text-start">
                    Option Graph
                </h4>
                <div>
                    <Select
                        allowClear
                        showSearch
                        className="text-start multi-user-dropdown h-10 w-60"
                        placeholder="Select Stock Symbol"
                        value={parentStockSymbol.current}
                        onChange={handleStockSymbolChange}
                        options={stocks}
                        open={stockSymbolOpen}
                        onDropdownVisibleChange={(visible) => setStockSymbolOpen(visible)}
                    />
                </div>
                <p>
                    $
                    {lastPrice !== null && lastPrice !== undefined
                        ? formatNumberWithCommas(lastPrice)
                        : 0}
                </p>
            </div>
            {loading ? (
                <p>Loading...</p>
            ) : !parentStockSymbol.current || processedData.length === 0 ? (
                <p>No data Found.</p>
            ) : (
                <div className="w-full h-[400px] sm:h-[500px] md:h-[600px] lg:h-[700px]">
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                            data={processedData}
                            margin={{ top: 20, right: 30, left: 30, bottom: 0 }}
                        >
                            <defs>
                                <linearGradient id="callGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8} />
                                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="putGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.8} />
                                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="expirationDate"
                                angle={-45}
                                textAnchor="end"
                                height={100}
                                fontSize={13}
                                tick={{ fill: '#4B5563' }}
                            />
                            <YAxis
                                yAxisId="price"
                                fontSize={13}
                                label={{
                                    value: 'Strike Price ($)',
                                    angle: -90,
                                    position: 'insideLeft',
                                    style: { fill: '#4B5563' },
                                }}
                                tick={{ fill: '#4B5563' }}
                            />
                            <YAxis
                                yAxisId="oi"
                                fontSize={13}
                                orientation="right"
                                label={{
                                    value: 'Open Interest',
                                    angle: 90,
                                    position: 'insideRight',
                                    style: { fill: '#4B5563' },
                                    dx: 20,  // Adjust this value to shift the label further right
                                }}
                                tick={{ fill: '#4B5563' }}
                            />

                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Line
                                yAxisId="price"
                                type="monotone"
                                dataKey="callStrike"
                                name="Call Strike"
                                stroke="#3B82F6"
                                strokeWidth={1}
                            />
                            <Area
                                yAxisId="oi"
                                type="monotone"
                                dataKey="callOI"
                                name="Call OI"
                                stroke="none"
                                fillOpacity={1}
                                fill="url(#callGradient)"
                            />
                            <Line
                                yAxisId="price"
                                type="monotone"
                                dataKey="putStrike"
                                name="Put Strike"
                                stroke="#EF4444"
                                strokeWidth={1}
                            />
                            <Area
                                yAxisId="oi"
                                type="monotone"
                                dataKey="putOI"
                                name="Put OI"
                                stroke="none"
                                fillOpacity={1}
                                fill="url(#putGradient)"
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export default OptionGraph;
